import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, fmtInt } from "@/config/site";
import { useLang } from "@/i18n";
import { SectionHead, Reveal } from "@/components/common";
import { Skeleton } from "@/components/ui/skeleton";

const CARDS = [
  { key: "total_supply", labelKey: "dash.totalSupply" },
  { key: "price", labelKey: "dash.price" },
  { key: "market_cap", labelKey: "dash.marketCap" },
  { key: "holders", labelKey: "dash.holders" },
  { key: "liquidity", labelKey: "dash.liquidity" },
  { key: "volume_24h", labelKey: "dash.volume" },
  { key: "nft_holders", labelKey: "dash.nftHolders" },
  { key: "nfts_minted", labelKey: "dash.nftsMinted" },
];

const RANGES = ["24H", "7D", "30D"];

export function LiveDashboard() {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState("24H");

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  return (
    <section className="section-pad relative py-24" data-testid="live-dashboard">
      <SectionHead eyebrow={t("dash.eyebrow")} title={t("dash.title")} sub={t("dash.sub")} />

      <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="stats-grid">
        {CARDS.map((c, i) => {
          const m = stats?.metrics?.[c.key];
          return (
            <motion.div
              key={c.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              className="card-surface p-5"
              data-testid={`stat-${c.key}`}
            >
              <p className="font-heading text-[10px] font-bold uppercase tracking-[0.25em] text-mist">{t(c.labelKey)}</p>
              {!stats ? (
                <Skeleton className="mt-4 h-8 w-3/4 bg-white/5" />
              ) : m?.value !== null && m?.value !== undefined ? (
                <p className="mt-3 font-mono text-xl font-bold text-neon sm:text-2xl">
                  {fmtInt(m.value)}
                  <span className="ml-2 text-[10px] font-medium uppercase tracking-widest text-mist">{m.unit}</span>
                </p>
              ) : (
                <p className="mt-3 font-mono text-sm font-bold uppercase tracking-wider text-goldlight/90 sm:text-base">
                  {t("common.awaitingLaunch")}
                </p>
              )}
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-mist/60">
                {m?.status === "PLANNED" ? t("common.planned") : m?.status || "—"}
              </p>
            </motion.div>
          );
        })}
      </div>

      <Reveal delay={0.15} className="mt-6">
        <div className="card-surface p-6" data-testid="price-chart">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.25em] text-paper">{t("dash.chartTitle")}</p>
            <div className="flex gap-2" role="tablist" aria-label="Range">
              {RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="tab"
                  aria-selected={range === r}
                  onClick={() => setRange(r)}
                  data-testid={`range-${r}`}
                  className={`mono-chip transition-colors ${range === r ? "border-neon/60 text-neon" : "hover:border-white/30"}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="relative mt-6 flex h-56 items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/10 bg-ink/60">
            <svg aria-hidden="true" className="absolute inset-0 h-full w-full opacity-25" preserveAspectRatio="none" viewBox="0 0 400 160">
              <defs>
                <linearGradient id="ch" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#A8FF3E" stopOpacity="0.35" />
                  <stop offset="1" stopColor="#A8FF3E" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0,150 L400,150 L400,160 L0,160 Z" fill="url(#ch)" />
              <path d="M0,150 L400,150" stroke="#A8FF3E" strokeWidth="1.5" strokeDasharray="4 6" fill="none" />
            </svg>
            <div className="relative z-10 max-w-sm px-6 text-center">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-goldlight">{t("common.awaitingLaunch")} · {range}</p>
              <p className="mt-3 text-xs leading-relaxed text-mist">{t("dash.chartEmpty")}</p>
            </div>
          </div>
          {stats?.updated_at && (
            <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-mist/60" data-testid="stats-updated">
              {t("dash.updated")}: {new Date(stats.updated_at).toLocaleString()} · Solana RPC
            </p>
          )}
        </div>
      </Reveal>
    </section>
  );
}
